import { z } from 'zod';
import { StoryHistoryEntrySchema } from './storyHistory';

// ============================================
// 故事历史压缩与块结构类型
// ============================================

/**
 * 历史条目当前所处的压缩层级。
 * raw 表示完整原文，session_summary 表示单会话摘要，
 * stage_summary 表示更高一级的阶段总结。
 */
export const StoryHistoryCompressionLayer = z.enum(['raw', 'session_summary', 'stage_summary']);
export type StoryHistoryCompressionLayerValue = z.infer<typeof StoryHistoryCompressionLayer>;

/**
 * 历史块类型。
 * session 表示单个会话块，stage 表示更高一级的阶段块。
 */
export const StoryHistoryBlockKind = z.enum(['session', 'stage']);
export type StoryHistoryBlockKindValue = z.infer<typeof StoryHistoryBlockKind>;

/**
 * 单个会话压缩单元。
 * bridgeSystemEntries 指上一个对话块之后、当前 session 开始之前的全部系统信息，
 * sessionEntries 则承载当前 session 的完整原文。
 */
export const StoryHistorySessionBlockSchema = z.object({
  id: z.string(),
  kind: z.literal('session'),
  compressionLayer: StoryHistoryCompressionLayer.default('raw'),
  sessionKey: z.string(),
  sceneName: z.string().optional(),
  participantIds: z.array(z.string()).default([]),
  startedAt: z.number(),
  endedAt: z.number(),
  startedGameTime: z.string().optional(),
  endedGameTime: z.string().optional(),
  bridgeSystemEntries: z.array(StoryHistoryEntrySchema).default([]),
  sessionEntries: z.array(StoryHistoryEntrySchema),
});
export type StoryHistorySessionBlock = z.infer<typeof StoryHistorySessionBlockSchema>;

/**
 * 更高一级的阶段压缩单元。
 * 这里聚合更远处的一组会话摘要块与其间残留的系统历史，
 * 后续可交由 AI 进一步压成阶段总结节点。
 */
export const StoryHistoryStageBlockSchema = z.object({
  id: z.string(),
  kind: z.literal('stage'),
  compressionLayer: StoryHistoryCompressionLayer.default('session_summary'),
  sceneNames: z.array(z.string()).default([]),
  participantIds: z.array(z.string()).default([]),
  startedAt: z.number(),
  endedAt: z.number(),
  startedGameTime: z.string().optional(),
  endedGameTime: z.string().optional(),
  entries: z.array(StoryHistoryEntrySchema),
});
export type StoryHistoryStageBlock = z.infer<typeof StoryHistoryStageBlockSchema>;
